import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { useToast } from '@/components/ui/Toast';
import { useForm } from '@/hooks/useForm';

import { useUpdateProfile } from '../../hooks/useAuthActions';
import { profileNameFormSchema } from '../../schemas/auth.schema';
import styles from './ProfileNameForm.module.css';

export interface ProfileNameFormProps {
  /** The name saved now — the field starts with it. */
  currentName: string | null;
}

/** Edits the signed-in user's own name, which the dashboard greeting and account menu show. */
export function ProfileNameForm({ currentName }: ProfileNameFormProps) {
  const form = useForm({ name: currentName ?? '' });
  const updateProfile = useUpdateProfile();
  const toast = useToast();

  const unchanged = form.values.name.trim() === (currentName ?? '');

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const values = form.parse(profileNameFormSchema);
        if (!values) return;
        updateProfile.mutate(values, {
          onSuccess: (profile) => {
            form.setValue('name', profile.name ?? '');
            toast.success('Name saved');
          },
          onError: form.handleError,
        });
      }}
    >
      <TextField
        label="Your name"
        hint="Shown on the dashboard and in the account menu."
        autoComplete="name"
        required
        fieldClassName={styles.field}
        {...form.field('name')}
      />
      {form.formError && <Alert tone="danger">{form.formError}</Alert>}
      <Button type="submit" isLoading={updateProfile.isPending} disabled={unchanged}>
        Save name
      </Button>
    </form>
  );
}
